import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '@/components/App';
import { useWeekStartsOn } from '@/hooks';
import { STAGES } from './constants';
import { useLazyQuery, useMutation } from '@apollo/client';
import { COMPLETED_ORDERS, UPDATE_ORDERS, GET_FULL_ORDER, GET_SHIPPERS, GET_CARRIERS } from './graphql';
import { UPSERT_ORDERS } from '@/components/ShipmentForm/graphql/mutations';
import { captureException } from '@sentry/react';
import { addMonths, endOfWeek, getWeek, startOfWeek, format, addDays } from 'date-fns';
import { asUTCDate } from '@/utilities/convertToISO';
import { CREATE_INVOICES } from '@/components/admin/AdminFinancials/receivables/graphql';
import { generateInvoices, MARKETPLACE_TIERS } from '@onward-delivery/core';
import { QUICKBOOKS_CSV_COLUMNS, useColumns } from './columns';
import { useNavigate } from 'react-router';
import { generateLineItemsOrders } from '../Invoices/utils';
import csvDownload from 'json-to-csv-export';
import * as ROUTES from '@/constants/routes';
import { ASSET_HEAVY } from '@/constants/featureFlags';
import { colors } from '@/styles';
import { auditQueueOrders, mostRecentRoute } from './utils';

export const Context = createContext();

// Normalize a UTC timestamp so its local year/month/day match the UTC date.
// Prevents timezone-dependent week shifts around midnight / DST boundaries.
const asUTCBillingDate = (timestamp) => {
    const d = new Date(timestamp);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

// Build a resolveBase callback for generateInvoices.
// Looks up week_starts_on per invoice's organization_id; falls back to 1 (Monday) for
// non-org buckets (ONWARD_*, CLIENT, INTERNAL) and unknown orgs.
const buildResolveBase = ({ ordersInScope, baseFields }) => {
    const orgWeekStartsOn = {};
    (ordersInScope || []).forEach((o) => {
        const orgId = o.order_shipper?.user?.organization_id;
        const ws = o.order_shipper?.user?.organization?.week_starts_on;
        if (orgId && ws !== undefined && ws !== null) orgWeekStartsOn[orgId] = ws;
    });

    return (invoice) => {
        const weekStartsOn = invoice.organization_id ? orgWeekStartsOn[invoice.organization_id] ?? 1 : 1;
        // billingDate is shared across all invoices in this batch (caller validated same week).
        const sow = startOfWeek(baseFields.billingDate, { weekStartsOn });
        const eow = endOfWeek(baseFields.billingDate, { weekStartsOn });
        const week = getWeek(baseFields.billingDate, { weekStartsOn });
        return {
            description: `Invoice for ${format(sow, 'yyyy-MM-dd')} - ${format(eow, 'yyyy-MM-dd')}`,
            due_date: asUTCDate(format(addDays(eow, 30), 'yyyy-MM-dd')).toISOString(),
            pay_period_start: asUTCDate(format(sow, 'yyyy-MM-dd')).toISOString(),
            pay_period_end: asUTCDate(format(eow, 'yyyy-MM-dd')).toISOString(),
            week_number: week,
            status: 'UNPAID',
            ...(baseFields.partner_client_id ? { partner_client_id: baseFields.partner_client_id } : {}),
        };
    };
};

const dateNumeric = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});

const TODAY = new Date(new Date().setHours(0, 0, 0, 0));

const PAGE_SIZE = 50;
const DEFAULT_SORT_BY = { id: 'completed_date', desc: true };

const ROUTE_ROW_PREFIX = 'route-row-';
const NON_ROUTED_KEY = 'non-routed';

const buildRouteRow = ({ key, route, ordersInGroup }) => {
    const totals = ordersInGroup.reduce(
        (acc, o) => {
            acc.shipper_rate += o.shipper_rate || 0;
            acc.carrier_rate += o.carrier_rate || 0;
            acc.contractor_rate += o.contractor_rate || 0;
            acc.middle_mile_shipper_rate += o.middle_mile_shipper_rate || 0;
            acc.middle_mile_carrier_rate += o.middle_mile_carrier_rate || 0;
            return acc;
        },
        {
            shipper_rate: 0,
            carrier_rate: 0,
            contractor_rate: 0,
            middle_mile_shipper_rate: 0,
            middle_mile_carrier_rate: 0,
        }
    );
    const first = ordersInGroup[0] || {};
    return {
        isRouteRow: true,
        order_id: `${ROUTE_ROW_PREFIX}${key}`,
        route: route || null,
        routeOrderCount: ordersInGroup.length,
        routeOrders: ordersInGroup,
        order_shipper: first.order_shipper,
        order_carrier: first.order_carrier,
        order_contractor: first.order_contractor,
        ...totals,
    };
};

const groupOrdersByRoute = (orders) => {
    const groups = new Map();
    const nonRouted = [];

    orders.forEach((order) => {
        const route = mostRecentRoute(order);
        if (route?.route_id) {
            if (!groups.has(route.route_id)) {
                groups.set(route.route_id, { route, items: [] });
            }
            groups.get(route.route_id).items.push(order);
        } else {
            nonRouted.push(order);
        }
    });

    const result = [];
    groups.forEach(({ route, items }, key) => {
        result.push(buildRouteRow({ key, route, ordersInGroup: items }));
        result.push(...items);
    });

    if (nonRouted.length > 0) {
        result.push(buildRouteRow({ key: NON_ROUTED_KEY, route: null, ordersInGroup: nonRouted }));
        result.push(...nonRouted);
    }

    return result;
};

export const ContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const { user, client, admin_clients, teammate } = useContext(UserContext);
    const weekStartsOn = useWeekStartsOn();
    const [hasMore, setHasMore] = useState(false);
    const [viewPod, viewOrderPod] = useState(null);
    const [editingOrder, setEditingOrder] = useState(null);
    const [selectedOrderIds, setSelectedOrderIds] = useState({});
    const [notification, setNotification] = useState({});

    const DEFAULT_FILTER = useMemo(
        () => ({
            stage: STAGES.PENDING_APPROVAL,
            completedStart: startOfWeek(addMonths(TODAY, -1), { weekStartsOn }),
            completedEnd: null,
            shippers: [],
            carriers: [],
            searchTerm: '',
        }),
        [weekStartsOn]
    );

    const [filter, setFilter] = useState(DEFAULT_FILTER);
    const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);

    const [isAdmin, isOnwardAdmin, isCarrierAdmin, isTeammateInvoicing] = useMemo(() => {
        const isAdmin = user?.roles?.['ADMIN'] && !client?.client_id;
        const isOnwardAdmin = user?.roles?.['ONWARD_ADMIN'] && !client?.client_id;
        const isTeammateInvoicing =
            !teammate?.teammate_id || ['admin', 'invoicing'].some((role) => teammate?.roles?.[role]);
        const isCarrierAdmin =
            client?.user?.roles?.['CARRIER'] && client?.user?.circles?.[ASSET_HEAVY] && isTeammateInvoicing;
        return [isAdmin, isOnwardAdmin, isCarrierAdmin, isTeammateInvoicing];
    }, [user, client, teammate]);

    const weeks = useMemo(() => {
        return Array(6)
            .fill(0)
            .map((_, idx) => {
                const day = addDays(TODAY, -1 * idx * 7);
                const week = getWeek(day, { weekStartsOn });
                const sow = startOfWeek(day, { weekStartsOn });
                const eow = endOfWeek(day, { weekStartsOn });

                return {
                    sow,
                    eow,
                    week_number: week,
                    label:
                        idx === 0
                            ? `Current Week (${dateNumeric.format(sow)} - ${dateNumeric.format(eow)})`
                            : `${dateNumeric.format(sow)} - ${dateNumeric.format(eow)}`,
                };
            });
    }, [weekStartsOn]);

    const [getCompletedOrders, { data, loading: initInFlight, fetchMore, refetch }] = useLazyQuery(COMPLETED_ORDERS, {
        onCompleted: () => {
            setSelectedOrderIds({});
        },
        onError: (err) => {
            setNotification({
                severity: 'error',
                message: 'Failed to load completed orders',
            });
            console.error(err);
            captureException(err);
        },
    });

    const [getShippers, { data: shipperData, loading: shippersInFlight }] = useLazyQuery(GET_SHIPPERS, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const [getCarriers, { data: carrierData, loading: carriersInFlight }] = useLazyQuery(GET_CARRIERS, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const [getFullOrder, { loading: fullOrderLoading }] = useLazyQuery(GET_FULL_ORDER, {
        onError: (err) => {
            console.error(err);
            captureException(err);
            setNotification({
                severity: 'error',
                message: 'Failed to load order details',
            });
        },
    });

    const [updateOrders, { loading: updateInFlight }] = useMutation(UPDATE_ORDERS, {
        onCompleted: () => {
            refetch();
        },
        onError: (err) => {
            setNotification({
                severity: 'error',
                message: 'Failed to update orders',
            });
            console.error(err);
            captureException(err);
        },
    });

    // Same mutation as updateOrders but WITHOUT onCompleted: refetch — used for
    // per-order approvals inside the audit queue so we don't refetch the whole
    // table on every approval. The table is refreshed once when the modal closes
    // (closeAuditModal calls refetch).
    const [updateOrdersQuiet] = useMutation(UPDATE_ORDERS);

    const [upsertOrder, { loading: upsertOrderLoading }] = useMutation(UPSERT_ORDERS, {
        refetchQueries: [COMPLETED_ORDERS],
        awaitRefetchQueries: true,
        onError: (error) => {
            captureException(error);
            console.error(error);
            setNotification({ message: 'Failed to update order. Please try again.', severity: 'error' });
        },
    });

    const [createInvoices, { loading: invoiceInFlight }] = useMutation(CREATE_INVOICES, {
        onCompleted: () => {
            refetch();
        },
        onError: (err) => {
            setNotification({
                severity: 'error',
                message: 'Failed to create invoices',
            });
            console.error(err);
            captureException(err);
        },
    });

    const orders = useMemo(() => {
        return data?.results || [];
    }, [data]);

    const [routeRatesEnabled, setRouteRatesEnabled] = useState(false);
    const [auditSession, setAuditSession] = useState(null);

    // The accessorial audit flow (queue + per-route modal, the Reviewed column, and the
    // batch "Approve Reviewed Orders" action) is limited to:
    //   - accounts with the asset-heavy flag
    //   - Onward admins
    //   - corporate admins (ADMIN role scoped to an organization)
    // Both admin checks already require no client to be selected, so while impersonating an
    // account the impersonated account's asset-heavy flag decides.
    const auditEnabled = useMemo(
        () => Boolean(client?.user?.circles?.[ASSET_HEAVY] || isOnwardAdmin || isAdmin),
        [client, isAdmin, isOnwardAdmin, user]
    );

    const displayedOrders = useMemo(() => {
        if (!routeRatesEnabled) return orders;
        return groupOrdersByRoute(orders);
    }, [orders, routeRatesEnabled]);

    const handleColor = useCallback(({ order }) => {
        if (order?.isRouteRow) {
            return [colors.greys.selected, colors.greys[4]];
        }
        return [];
    }, []);

    const editableOrders = useMemo(() => {
        if (isOnwardAdmin || isAdmin) {
            return Object.fromEntries(orders.map((order) => [order.order_id, true]));
        }

        if (isCarrierAdmin) {
            return Object.fromEntries(
                orders.map((order) => [
                    order.order_id,
                    !!order.oms ||
                        ![MARKETPLACE_TIERS.T3_FULLY_MANAGED, MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING].includes(
                            order.shipper_rate_override?.marketplace_tier
                        ),
                ])
            );
        }

        return Object.fromEntries(orders.map((order) => [order.order_id, !!order.oms]));
    }, [orders, client, isAdmin, isOnwardAdmin, isCarrierAdmin]);

    const setSelectedRealOrderIds = useCallback(
        (nextOrUpdater) => {
            setSelectedOrderIds((prev) => {
                const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
                return Object.fromEntries(
                    Object.entries(next || {}).filter(([id, v]) => {
                        if (!v || id.startsWith(ROUTE_ROW_PREFIX)) return false;
                        // Carrier admins cannot select restricted T2/T3 orders for export or invoicing.
                        if (isCarrierAdmin && !editableOrders[id]) return false;
                        return true;
                    })
                );
            });
        },
        [isCarrierAdmin, editableOrders]
    );

    // IDs of displayed rows that a carrier admin is allowed to select.
    // Non-carrier-admin users can select any non-route row.
    const selectableDisplayedIds = useMemo(() => {
        return displayedOrders
            .filter((o) => !o.isRouteRow)
            .map((o) => o.order_id)
            .filter((id) => !isCarrierAdmin || editableOrders[id]);
    }, [displayedOrders, isCarrierAdmin, editableOrders]);

    const selectionStateRef = useRef({ selectedOrderIds, selectableDisplayedIds });

    useEffect(() => {
        selectionStateRef.current = { selectedOrderIds, selectableDisplayedIds };
    }, [selectedOrderIds, selectableDisplayedIds]);

    const setSelectedRealOrderIdsRef = useRef(setSelectedRealOrderIds);
    setSelectedRealOrderIdsRef.current = setSelectedRealOrderIds;

    const selectionListenersRef = useRef(new Set());

    useEffect(() => {
        selectionListenersRef.current.forEach((l) => l());
    }, [selectedOrderIds, selectableDisplayedIds]);

    const selectionController = useMemo(
        () => ({
            subscribe: (listener) => {
                selectionListenersRef.current.add(listener);
                return () => selectionListenersRef.current.delete(listener);
            },
            isRowSelected: (id) => !!selectionStateRef.current.selectedOrderIds[id],
            isRowDisabled: (id) => !selectionStateRef.current.selectableDisplayedIds.includes(id),
            getHeaderState: () => {
                const { selectedOrderIds: sel, selectableDisplayedIds: ids } = selectionStateRef.current;
                if (!ids.length) return 'none';
                const selectedCount = ids.filter((id) => !!sel[id]).length;
                if (selectedCount === 0) return 'none';
                if (selectedCount === ids.length) return 'all';
                return 'some';
            },
            toggleRow: (id) => {
                if (!selectionStateRef.current.selectableDisplayedIds.includes(id)) return;
                setSelectedRealOrderIdsRef.current((prev) => {
                    const next = { ...prev };
                    if (next[id]) delete next[id];
                    else next[id] = true;
                    return next;
                });
            },
            toggleAll: () => {
                const { selectedOrderIds: sel, selectableDisplayedIds: ids } = selectionStateRef.current;
                const allSelected = ids.length > 0 && ids.every((id) => !!sel[id]);
                if (allSelected) {
                    setSelectedRealOrderIdsRef.current({});
                } else {
                    setSelectedRealOrderIdsRef.current(Object.fromEntries(ids.map((id) => [id, true])));
                }
            },
        }),
        []
    );

    const canEditPerspective = useCallback(
        (order, perspective) => {
            if (isAdmin || isOnwardAdmin) {
                return true;
            }
            switch (perspective) {
                case 'shipper':
                case 'mm_shipper':
                case 'wh_shipper':
                    if (isCarrierAdmin) {
                        return (
                            !!order.oms ||
                            ![MARKETPLACE_TIERS.T3_FULLY_MANAGED, MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING].includes(
                                order.shipper_rate_override?.marketplace_tier
                            )
                        );
                    }
                    return order.shipper_id === client?.client_id && isTeammateInvoicing;
                case 'carrier':
                    return isCarrierAdmin || (order.carrier_id === client?.client_id && isTeammateInvoicing);
                case 'mm_carrier':
                    return (
                        isCarrierAdmin || (order.middle_mile_carrier_id === client?.client_id && isTeammateInvoicing)
                    );
                case 'wh_carrier':
                    return isCarrierAdmin || (order.warehouse_carrier_id === client?.client_id && isTeammateInvoicing);
                case 'contractor':
                    return isCarrierAdmin || (order.contractor_id === client?.client_id && isTeammateInvoicing);
                case 'internal':
                    return true;
            }
        },
        [client, isCarrierAdmin, isAdmin, isOnwardAdmin, isTeammateInvoicing]
    );

    const canViewAllBreakdowns = useMemo(
        () => Boolean(isAdmin || isOnwardAdmin || isCarrierAdmin),
        [isAdmin, isOnwardAdmin, isCarrierAdmin]
    );

    const shippers = useMemo(() => {
        const shippers =
            (shipperData?.results || [])
                .filter((order) => !!order?.order_shipper)
                .map((order) => {
                    return {
                        label: order?.order_shipper?.business_name || 'Unknown',
                        value: order.shipper_id,
                    };
                })
                .sort((l, r) => l.label.localeCompare(r.label)) || [];

        return shippers;
    }, [shipperData]);

    const carriers = useMemo(() => {
        const carriers =
            (carrierData?.results || [])
                .filter((order) => !!order?.order_carrier)
                .map((order) => {
                    return {
                        label: order?.order_carrier?.business_name || 'Unknown',
                        value: order.carrier_id,
                    };
                })
                .sort((l, r) => l.label.localeCompare(r.label)) || [];

        return carriers;
    }, [carrierData]);

    // Base where: everything except shipper/carrier selection filters.
    // Used to build dropdown queries so selecting a shipper doesn't
    // narrow the shipper dropdown to only that shipper (and likewise
    // for carriers).
    const baseWhere = useMemo(() => {
        let where = [{ order_status: { _in: ['complete', 'routedComplete', 'cancelled', 'failed'] } }];

        if (isOnwardAdmin) {
            where.push({ oms: { _eq: false } });
            where.push({
                shipper_rate_override: {
                    marketplace_tier: {
                        _in: [MARKETPLACE_TIERS.T3_FULLY_MANAGED, MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING],
                    },
                },
            });
        } else if (isAdmin) {
            where.push({ oms: { _eq: false } });
            where.push({
                shipper_rate_override: {
                    marketplace_tier: {
                        _in: [MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY, MARKETPLACE_TIERS.T2_INTEGRATIONS_ONLY],
                    },
                },
            });
            where.push({
                shipper_id: {
                    _in: (admin_clients || [])
                        .filter((client) => client?.user?.roles?.['SHIPPER'])
                        .map((client) => client.client_id),
                },
            });
        } else if (client?.client_id) {
            where.push({
                _or: [
                    ...(client?.user?.roles?.['SHIPPER'] ? [{ shipper_id: { _eq: client.client_id } }] : []),
                    ...(client?.user?.roles?.['CARRIER'] ? [{ carrier_id: { _eq: client.client_id } }] : []),
                    ...(client?.user?.roles?.['WAREHOUSE_CARRIER']
                        ? [{ warehouse_carrier_id: { _eq: client.client_id } }]
                        : []),
                ],
            });
        }

        switch (filter.stage) {
            case STAGES.PENDING_APPROVAL:
                where.push({ shipper_invoice_id: { _is_null: true } });
                where.push({ invoicing_status: { _neq: 'APPROVED' } });
                break;
            case STAGES.READY_TO_INVOICE:
                where.push({ shipper_invoice_id: { _is_null: true } });
                where.push({ invoicing_status: { _eq: 'APPROVED' } });
                break;
            case STAGES.INVOICED:
                where.push({ shipper_invoice: { status: { _in: ['DRAFT', 'UNPAID'] } } });
                break;
            case STAGES.APPROVED:
                where.push({ shipper_invoice: { status: { _eq: 'APPROVED' } } });
                break;
            case STAGES.PAID:
                where.push({ shipper_invoice: { status: { _eq: 'PAID' } } });
                break;
        }

        if (filter.completedStart) {
            where.push({ billing_completion_time: { _gte: filter.completedStart } });
        }

        if (filter.completedEnd) {
            where.push({ billing_completion_time: { _lte: filter.completedEnd } });
        }

        if (filter.searchTerm) {
            where.push({
                _or: ['order_number', 'po_number', 'reference_id', 'dropoff_name'].map((attr) => ({
                    [attr]: { _ilike: `%${filter.searchTerm.trim()}%` },
                })),
            });
        }

        if (filter.week) {
            where.push({ billing_completion_time: { _gte: filter.week.sow } });
            where.push({ billing_completion_time: { _lte: filter.week.eow } });
        }

        return where;
    }, [filter, admin_clients, client, isAdmin, isOnwardAdmin]);

    // Full where for the orders table: base + shipper + carrier filters.
    const where = useMemo(() => {
        const w = [...baseWhere];
        if (filter.shippers?.length) {
            w.push({ shipper_id: { _in: filter.shippers } });
        }
        if (filter.carriers?.length) {
            w.push({ carrier_id: { _in: filter.carriers } });
        }
        return { _and: w };
    }, [baseWhere, filter.shippers, filter.carriers]);

    // Shipper dropdown: base + carrier filter only (excludes shipper filter).
    const shipperWhere = useMemo(() => {
        const w = [...baseWhere];
        if (filter.carriers?.length) {
            w.push({ carrier_id: { _in: filter.carriers } });
        }
        return { _and: w };
    }, [baseWhere, filter.carriers]);

    // Carrier dropdown: base + shipper filter only (excludes carrier filter).
    const carrierWhere = useMemo(() => {
        const w = [...baseWhere];
        if (filter.shippers?.length) {
            w.push({ shipper_id: { _in: filter.shippers } });
        }
        return { _and: w };
    }, [baseWhere, filter.shippers]);

    const order_by = useMemo(() => {
        const ascOrDesc = sortBy?.desc ? 'desc_nulls_last' : 'asc_nulls_last';
        switch (sortBy?.id) {
            case 'completed_date':
                return [{ billing_completion_time: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'shipper-fm-total':
                return [{ shipper_rate: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'carrier-fm-total':
                return [{ carrier_rate: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'shipper-mm-total':
                return [{ middle_mile_shipper_rate: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'carrier-mm-total':
                return [{ middle_mile_carrier_rate: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'fm-margin':
                return [
                    { shipper_rate: ascOrDesc },
                    { carrier_rate: sortBy.desc ? 'asc_nulls_last' : 'desc_nulls_last' },
                    { order_number: 'desc_nulls_last' },
                ];
            case 'mm-margin':
                return [
                    { middle_mile_shipper_rate: ascOrDesc },
                    { middle_mile_carrier_rate: sortBy.desc ? 'asc_nulls_last' : 'desc_nulls_last' },
                    { order_number: 'desc_nulls_last' },
                ];
            default:
                return [{ billing_completion_time: 'desc_nulls_last' }, { order_number: 'desc_nulls_last' }];
        }
    }, [sortBy]);

    useEffect(() => {
        getCompletedOrders({
            variables: {
                where,
                limit: PAGE_SIZE,
                offset: 0,
                order_by,
            },
        }).then(({ data }) => {
            setHasMore(data?.results?.length === PAGE_SIZE);
        });

        getShippers({ variables: { where: shipperWhere } });
        getCarriers({ variables: { where: carrierWhere } });
    }, [where, shipperWhere, carrierWhere, order_by]);

    const loadMore = useCallback(() => {
        fetchMore({
            variables: {
                where,
                limit: PAGE_SIZE,
                offset: data?.results?.length || 0,
                order_by,
            },
            updateQuery: (data, { fetchMoreResult }) => {
                const prev = Object.fromEntries((data.results || []).map((order) => [order.order_id, true]));
                const clone = [
                    ...(data.results || []),
                    ...fetchMoreResult.results.filter((order) => !prev[order.order_id]),
                ];
                return {
                    results: clone,
                };
            },
        }).then((result) => {
            if (result.data.results.length < PAGE_SIZE) {
                setHasMore(false);
            }
        });
    }, [where, order_by, fetchMore, PAGE_SIZE, data]);

    const callbacks = {
        setFilter,
        setSortBy,
        setRouteRatesEnabled,
        setSelectedOrderIds: setSelectedRealOrderIds,
        setNotification,
        loadMore,
        selectRows: setSelectedRealOrderIds,
        getRowId: (order) => order.order_id,
        viewOrderPod,
        canEditPerspective,
        handleColor,
        editOrder: (order) => {
            if (order) {
                // Same per-order rule the Approve / Unapprove actions use. A viewer who only sees
                // this row because they're on one leg of it can't open the full-order editor.
                if (!editableOrders[order.order_id]) return;
                getFullOrder({ variables: { order_id: order.order_id } }).then(({ data }) => {
                    setEditingOrder(data?.result || order);
                });
            } else {
                setEditingOrder(null);
            }
        },
        upsertOrder,
        gotoAccessorial: (order, perspective) => {
            navigate(`/accounting/accessorials/${order.order_id}/${perspective}`);
        },
        gotoDetails: (id) => {
            navigate(`/order/${id}`, { target: '_blank' });
        },
        gotoInvoice: (id) => {
            const path = ROUTES.ACCOUNTING_INVOICE.replace(':invoice_id', id);
            navigate(path, { target: '_blank' });
        },
        openAuditModal: (routeRow) =>
            setAuditSession({ mode: 'route', route: routeRow.route, orders: routeRow.routeOrders || [] }),
        openAuditQueue: () =>
            setAuditSession({ mode: 'queue', route: null, orders: auditQueueOrders(orders, selectedOrderIds) }),
        closeAuditModal: () => {
            setAuditSession(null);
            refetch();
        },
        // Mark a single order reviewed (queue mode) without moving it to
        // ready_to_invoice — the batch "Approve Reviewed Orders" action does that.
        // Returns true on success so the modal only drops the order from its queue
        // when the write lands.
        approveAuditOrder: async (orderId) => {
            try {
                await updateOrdersQuiet({
                    variables: {
                        order_ids: [orderId],
                        patch: { invoicing_status: 'PRE_APPROVED' },
                    },
                });
                return true;
            } catch (err) {
                setNotification({ severity: 'error', message: 'Failed to mark order reviewed' });
                console.error(err);
                captureException(err);
                return false;
            }
        },
        approveOrderIds: (orderIds) => {
            updateOrders({
                variables: { order_ids: orderIds, patch: { ready_to_invoice: true, invoicing_status: 'APPROVED' } },
            });
        },
        approveForInvoicing: () => {
            const ids = Object.keys(selectedOrderIds).filter((id) => selectedOrderIds[id]);
            updateOrders({
                variables: { order_ids: ids, patch: { ready_to_invoice: true, invoicing_status: 'APPROVED' } },
            });
        },
        approveReviewedOrders: () => {
            const ids = orders
                .filter((order) => order.invoicing_status === 'PRE_APPROVED')
                .map((order) => order.order_id);
            if (!ids.length) {
                setNotification({ severity: 'error', message: 'No reviewed orders to approve.' });
                return;
            }
            updateOrders({
                variables: { order_ids: ids, patch: { ready_to_invoice: true, invoicing_status: 'APPROVED' } },
            });
        },
        approveId: (id) => {
            updateOrders({
                variables: { order_ids: [id], patch: { ready_to_invoice: true, invoicing_status: 'APPROVED' } },
            });
        },
        invoiceId: (id) => {
            const order = orders.find((order) => order.order_id === id);
            if (!order || !order.billing_completion_time) {
                setNotification({
                    severity: 'error',
                    message: 'Order not found or missing billing completion date.',
                });
                return;
            }
            const billingDate = asUTCBillingDate(order.billing_completion_time);
            const toInvoice = orders.filter((order) => order.order_id === id);
            const resolveBase = buildResolveBase({
                ordersInScope: toInvoice,
                baseFields: {
                    billingDate,
                    partner_client_id: isCarrierAdmin ? client.client_id : null,
                },
            });
            const newInvoices = generateInvoices(resolveBase, toInvoice, []);
            return createInvoices({ variables: { invoices: newInvoices } });
        },
        createInvoices: () => {
            const toInvoice = orders.filter((order) => selectedOrderIds[order.order_id]);

            if (toInvoice.length === 0) {
                setNotification({ severity: 'error', message: 'No orders selected.' });
                return;
            }

            if (toInvoice.some((order) => !order.billing_completion_time)) {
                setNotification({
                    severity: 'error',
                    message: 'One or more selected orders are missing a billing completion date.',
                });
                return;
            }

            // Validate all orders are in the same billing week
            if (isOnwardAdmin) {
                const orderWeeks = toInvoice.map((order) => {
                    const billingDate = asUTCBillingDate(order.billing_completion_time);
                    return startOfWeek(billingDate, { weekStartsOn }).getTime();
                });
                const uniqueWeeks = [...new Set(orderWeeks)];
                if (uniqueWeeks.length > 1) {
                    setNotification({
                        severity: 'error',
                        message:
                            'Cannot create invoice for orders from different billing weeks. Please select orders from the same week.',
                    });
                    return;
                }
            }

            const billingDate = asUTCBillingDate(toInvoice[0].billing_completion_time);
            const resolveBase = buildResolveBase({
                ordersInScope: toInvoice,
                baseFields: {
                    billingDate,
                    partner_client_id: isCarrierAdmin ? client.client_id : null,
                },
            });
            const newInvoices = generateInvoices(resolveBase, toInvoice, []);
            return createInvoices({ variables: { invoices: newInvoices } });
        },
        createMultipleInvoices: () => {
            const toInvoice = orders.filter((order) => selectedOrderIds[order.order_id]);

            if (toInvoice.length === 0) {
                setNotification({ severity: 'error', message: 'No orders selected.' });
                return;
            }

            if (toInvoice.some((order) => !order.billing_completion_time)) {
                setNotification({
                    severity: 'error',
                    message: 'One or more selected orders are missing a billing completion date.',
                });
                return;
            }

            // Create one invoice per order
            const allInvoices = toInvoice.flatMap((order) => {
                const billingDate = asUTCBillingDate(order.billing_completion_time);
                const resolveBase = buildResolveBase({
                    ordersInScope: [order],
                    baseFields: { billingDate, partner_client_id: null },
                });
                return generateInvoices(resolveBase, [order], []);
            });

            return createInvoices({ variables: { invoices: allInvoices } });
        },
        exportCsv: () => {
            let toExport = orders.filter((order) => selectedOrderIds[order.order_id]);
            // Carrier admins must not export shipper-side figures for T2/T3 orders.
            if (isCarrierAdmin) {
                toExport = toExport.filter((order) => editableOrders[order.order_id]);
            }
            const exportData = generateLineItemsOrders(toExport);
            csvDownload({
                headers: QUICKBOOKS_CSV_COLUMNS.map((col) => col.header),
                filename: `completed-orders-${format(new Date(), 'yyyy-MM-dd')}.csv`,
                delimiter: ',',
                data: exportData.map((row) =>
                    QUICKBOOKS_CSV_COLUMNS.map((col) => {
                        const value = col.getValue(row);
                        return col.format ? col.format(value) : value;
                    })
                ),
            });
        },
    };

    const COLUMNS = useColumns({
        state: {
            editableOrders,
            isAdmin: isAdmin || isOnwardAdmin,
            isCarrierAdmin,
            routeRatesEnabled,
            auditEnabled,
            invoicingPermissions:
                isAdmin || isOnwardAdmin || isCarrierAdmin || client?.user?.circles?.['accounting-v2'],
        },
        callbacks,
        filter,
    });

    return (
        <Context.Provider
            value={{
                state: {
                    userId: user?.user_id,
                    routeRatesEnabled,
                    invoicingPermissions:
                        isAdmin || isOnwardAdmin || isCarrierAdmin || client?.user?.circles?.['accounting-v2'],
                    canViewAllBreakdowns,
                    filter,
                    defaultFilter: DEFAULT_FILTER,
                    sortBy,
                    shippers,
                    carriers,
                    weeks,
                    viewPod,
                    editingOrder,
                    selectedOrderIds,
                    editableOrders,
                    notification,
                    hasMore,
                    orders: displayedOrders,
                    columns: COLUMNS,
                    auditEnabled,
                    auditSession,
                    selectionController,
                },
                loading: {
                    init: initInFlight || shippersInFlight || carriersInFlight,
                    action: updateInFlight || invoiceInFlight || upsertOrderLoading,
                    editOrder: fullOrderLoading,
                },
                callbacks,
            }}
        >
            {children}
        </Context.Provider>
    );
};
